import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Pencil, Plus, ShieldCheck, Trash2, Users } from 'lucide-react'
import {
  accessGroupsQueryOptions,
  createDepartment,
  deleteDepartment,
  departmentsQueryOptions,
  updateDepartment,
} from '@/utils/departmentQueryOptions'
import { groupsQueryOptions } from '@/utils/recipientQueryOptions'
import type { Department } from '@/types/departmentTypes'
import { useShellStore } from '@/stores/shellStore'
import { useUserStore } from '@/stores/userStore'
import { cn } from '@/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Skeleton } from '@/components/ui/skeleton'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

/**
 * Super-admin CRUD for department permissions: who may send to which groups.
 *
 * Two lists, deliberately separate. The access groups are `SMS.Access.*` Entra
 * groups and answer "who is in this department" — membership stays in Entra
 * where IT already manages it. The scope groups answer "what may they text",
 * which is a decision about this app and is what gets stored here.
 *
 * As with templates, this dialog only hides UI. The backend re-checks
 * super-admin on every write here, and enforces the resulting scope on every
 * send — filtering a picker is not a permission.
 */
export function ManageDepartmentsDialog() {
  const open = useShellStore((s) => s.departmentsOpen)
  const close = useShellStore((s) => s.closeDepartments)
  const isSuperAdmin = useUserStore((s) => s.User?.isSuperAdmin ?? false)
  const queryClient = useQueryClient()

  // null = list view; a department = editing it; 'new' = creating one.
  const [editing, setEditing] = useState<Department | 'new' | null>(null)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [accessIds, setAccessIds] = useState<string[]>([])
  const [scopeIds, setScopeIds] = useState<string[]>([])
  const [scopeFilter, setScopeFilter] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [pendingDelete, setPendingDelete] = useState<Department | null>(null)

  const { data, status } = useQuery({ ...departmentsQueryOptions(), enabled: open })
  const departments = data?.departments ?? []

  // Only fetched while the editor is open — neither list is needed to browse.
  const editorOpen = open && editing !== null
  const { data: accessData } = useQuery({
    ...accessGroupsQueryOptions(),
    enabled: editorOpen,
  })
  const { data: allGroups } = useQuery({ ...groupsQueryOptions(), enabled: editorOpen })

  const accessGroups = accessData?.accessGroups ?? []

  const scopeChoices = useMemo(() => {
    const groups = allGroups ?? []
    const q = scopeFilter.trim().toLowerCase()
    const matching = q
      ? groups.filter((g) => g.displayName.toLowerCase().includes(q))
      : groups
    // Selected groups stay pinned at the top: a filter must never hide what you
    // have already granted, or it is easy to save a scope you can't see.
    return [...matching].sort((a, b) => {
      const aSel = scopeIds.includes(a.id) ? 0 : 1
      const bSel = scopeIds.includes(b.id) ? 0 : 1
      return aSel - bSel || a.displayName.localeCompare(b.displayName)
    })
  }, [allGroups, scopeFilter, scopeIds])

  const backToList = () => {
    setEditing(null)
    setName('')
    setDescription('')
    setAccessIds([])
    setScopeIds([])
    setScopeFilter('')
    setError(null)
  }

  const onOpenChange = (o: boolean) => {
    if (!o) {
      close()
      backToList()
    }
  }

  const startNew = () => {
    setEditing('new')
    setName('')
    setDescription('')
    setAccessIds([])
    setScopeIds([])
    setScopeFilter('')
    setError(null)
  }

  const startEdit = (d: Department) => {
    setEditing(d)
    setName(d.name)
    setDescription(d.description ?? '')
    setAccessIds(d.accessGroups.map((g) => g.id))
    setScopeIds(d.scopeGroups.map((g) => g.id))
    setScopeFilter('')
    setError(null)
  }

  const toggle = (list: string[], id: string) =>
    list.includes(id) ? list.filter((x) => x !== id) : [...list, id]

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['departments'] })

  const saveMutation = useMutation({
    mutationFn: () => {
      const input = {
        name: name.trim(),
        description: description.trim(),
        accessGroupIds: accessIds,
        scopeGroupIds: scopeIds,
      }
      return editing === 'new' || editing === null
        ? createDepartment(input)
        : updateDepartment(editing.id, input)
    },
    onSuccess: () => {
      invalidate()
      backToList()
    },
    onError: (e: Error) => setError(e.message),
  })

  const deleteMutation = useMutation({
    mutationFn: (d: Department) => deleteDepartment(d.id),
    onSuccess: () => {
      invalidate()
      setPendingDelete(null)
    },
    onError: (e: Error) => {
      setError(e.message)
      setPendingDelete(null)
    },
  })

  // A department with no access group admits nobody; one with no scope group
  // lets its members send nothing. Both are almost certainly a mistake, so
  // neither can be saved.
  const canSave =
    name.trim().length > 0 &&
    accessIds.length > 0 &&
    scopeIds.length > 0 &&
    !saveMutation.isPending

  if (!isSuperAdmin) return null

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[85vh] max-w-2xl grid-rows-[auto_minmax(0,1fr)_auto]">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit department' : 'Departments'}</DialogTitle>
            <DialogDescription>
              {editing
                ? 'Members of the access groups may send to the groups you pick below — and to the people in them. Nothing else.'
                : departments.length > 0
                  ? `${departments.length} department${departments.length === 1 ? '' : 's'}. Each one lets a set of staff message only their own groups.`
                  : 'Give a set of staff permission to message only their own groups.'}
            </DialogDescription>
          </DialogHeader>

          {editing ? (
            <div className="min-h-0 space-y-4 overflow-y-auto pr-1">
              <div className="space-y-1.5">
                <Label htmlFor="departmentName">Name</Label>
                <Input
                  id="departmentName"
                  placeholder="e.g. Emergency"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={120}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="departmentDescription">Description</Label>
                <Input
                  id="departmentDescription"
                  placeholder="Optional — what this department covers"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>

              <div className="space-y-1.5">
                <Label className="flex items-center gap-1.5">
                  <ShieldCheck className="h-3.5 w-3.5 text-muted-foreground" />
                  Who is in this department
                </Label>
                <p className="text-xs text-muted-foreground">
                  Entra groups named <code className="font-mono">SMS.Access.*</code>. Add
                  people to these in Entra — they don’t need to be in the all-access group.
                </p>
                {accessGroups.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-border px-4 py-6 text-center text-xs text-muted-foreground">
                    No <code className="font-mono">SMS.Access.*</code> groups found. Create one
                    in Entra — for example <code className="font-mono">SMS.Access.ED</code> —
                    then reopen this dialog.
                  </div>
                ) : (
                  <div className="max-h-40 overflow-y-auto rounded-xl border border-border">
                    {accessGroups.map((g) => (
                      /* The row is the control and the checkbox is decoration:
                         a Radix checkbox inside a <label> receives the click
                         twice — once itself, once re-dispatched by the label —
                         and toggles back to where it started. */
                      <button
                        key={g.id}
                        type="button"
                        role="checkbox"
                        aria-checked={accessIds.includes(g.id)}
                        onClick={() => setAccessIds((v) => toggle(v, g.id))}
                        className="flex w-full items-center gap-2.5 border-b border-border-2 px-3.5 py-2.5 text-left transition-colors last:border-b-0 hover:bg-surface-2"
                      >
                        <Checkbox
                          checked={accessIds.includes(g.id)}
                          tabIndex={-1}
                          className="pointer-events-none"
                        />
                        <span className="min-w-0 truncate text-sm">{g.displayName}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-1.5">
                <Label className="flex items-center gap-1.5">
                  <Users className="h-3.5 w-3.5 text-muted-foreground" />
                  What they may message
                  {scopeIds.length > 0 && (
                    <span className="rounded-full bg-primary/10 px-1.5 py-px text-[10px] font-bold text-primary">
                      {scopeIds.length} selected
                    </span>
                  )}
                </Label>
                <Input
                  placeholder="Filter groups…"
                  value={scopeFilter}
                  onChange={(e) => setScopeFilter(e.target.value)}
                />
                {!allGroups ? (
                  <Skeleton className="h-40 w-full" />
                ) : (
                  <div className="max-h-56 overflow-y-auto rounded-xl border border-border">
                    {scopeChoices.length === 0 ? (
                      <p className="px-3.5 py-6 text-center text-xs text-muted-foreground">
                        No groups match “{scopeFilter}”.
                      </p>
                    ) : (
                      scopeChoices.map((g) => (
                        <button
                          key={g.id}
                          type="button"
                          role="checkbox"
                          aria-checked={scopeIds.includes(g.id)}
                          onClick={() => setScopeIds((v) => toggle(v, g.id))}
                          className={cn(
                            'flex w-full items-center gap-2.5 border-b border-border-2 px-3.5 py-2.5 text-left transition-colors last:border-b-0 hover:bg-surface-2',
                            scopeIds.includes(g.id) && 'bg-surface-2',
                          )}
                        >
                          <Checkbox
                            checked={scopeIds.includes(g.id)}
                            tabIndex={-1}
                            className="pointer-events-none"
                          />
                          <span className="min-w-0 truncate text-sm">{g.displayName}</span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>

              {error && (
                <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {error}
                </div>
              )}
            </div>
          ) : (
            <div className="flex min-h-0 flex-col gap-3">
              {status === 'pending' ? (
                <Skeleton className="h-48 w-full" />
              ) : departments.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border px-6 py-10 text-center">
                  <ShieldCheck className="mx-auto h-6 w-6 text-faint" />
                  <p className="mt-2 text-sm font-semibold">No departments yet</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Everyone signing in today has access to every group. Add a department to
                    give a team access to only their own.
                  </p>
                </div>
              ) : (
                <div className="min-h-0 flex-1 overflow-y-auto rounded-xl border border-border">
                  {departments.map((d) => (
                    <div
                      key={d.id}
                      className="flex items-start gap-3 border-b border-border-2 px-3.5 py-3 transition-colors last:border-b-0 hover:bg-surface-2"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-bold">{d.name}</p>
                        {d.description && (
                          <p className="mt-0.5 truncate text-xs text-muted-foreground">
                            {d.description}
                          </p>
                        )}
                        <p className="mt-1 text-xs leading-[1.5] text-muted-foreground">
                          {d.accessGroups.length === 0 ? (
                            <span className="text-destructive">No access group — nobody is in this department</span>
                          ) : (
                            d.accessGroups.map((g) => g.displayName || g.id).join(', ')
                          )}
                        </p>
                        <p className="mt-0.5 text-xs leading-[1.5] text-muted-foreground">
                          {d.scopeGroups.length === 0 ? (
                            <span className="text-destructive">No groups assigned — its members can’t send anything</span>
                          ) : (
                            `Can message ${d.scopeGroups.length} group${d.scopeGroups.length === 1 ? '' : 's'}`
                          )}
                        </p>
                      </div>
                      <div className="flex flex-none gap-1">
                        <button
                          type="button"
                          onClick={() => startEdit(d)}
                          aria-label={`Edit ${d.name}`}
                          title="Edit"
                          className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-surface hover:text-foreground"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setPendingDelete(d)}
                          aria-label={`Delete ${d.name}`}
                          title="Delete"
                          className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-destructive transition-colors hover:bg-destructive/10"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {error && (
                <div className="flex-none rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {error}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {editing ? (
              <>
                <Button variant="outline" onClick={backToList}>
                  Cancel
                </Button>
                <Button onClick={() => saveMutation.mutate()} disabled={!canSave}>
                  {saveMutation.isPending
                    ? 'Saving…'
                    : editing === 'new'
                      ? 'Create department'
                      : 'Save changes'}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  Close
                </Button>
                <Button onClick={startNew}>
                  <Plus className="mr-2 h-4 w-4" />
                  New department
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={pendingDelete != null}
        onOpenChange={(o) => !o && setPendingDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{pendingDelete?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              Its members lose access to the app on their next request, unless they are also in
              the all-access group. Messages already sent are unaffected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => pendingDelete && deleteMutation.mutate(pendingDelete)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
